import React, { useState, useCallback, useEffect } from "react"
import { TfiWrite } from "react-icons/tfi"
import { CiSaveDown2 } from "react-icons/ci"
import { getDateFormatted } from "../../utils/generalUtils"
import { getFirstOpenDateToday, getFirstOpenDateTomorrow } from "../../utils/generalUtils"
import { useAuth0 } from "@auth0/auth0-react"
import useLogging from "../../hooks/useLogging"
import useFetchSettings from "../../hooks/useFetchSettings"

function NotesSection() {
	const { getAccessTokenSilently, user } = useAuth0()
	const { settingsData } = useFetchSettings()
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []
	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const date = getDateFormatted(
		getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)
	)
	const date_tomorrow = getDateFormatted(
		getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
	)
	const [notes, setNotes] = useState("")
	const logging = useLogging()

	// Load notes when the component mounts
	useEffect(() => {
		const loadNotes = async () => {
			try {
				const token = await getAccessTokenSilently({
					audience: process.env.REACT_APP_API_IDENTIFIER
				})
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/api/get-notes?date=${date}`,
					{
						method: "GET",
						headers: {
							Authorization: `Bearer ${token}`
						}
					}
				)
				if (!response.ok) {
					throw new Error("Failed to load notes")
				}
				const data = await response.json()
				logging.info("notes", {
					labels: {
						notes: data.notes
					}
				})
				setNotes(data.notes) // Assuming the response contains a 'notes' field
			} catch (error) {
				logging.error("Error loading notes:", error)
			}
		}
		loadNotes()
		// Mount-only effect — intentionally runs once.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Simple local state update
	const handleChange = useCallback(e => {
		setNotes(e.target.value)
	}, [])

	const handleSaveNotes = useCallback(async () => {
		try {
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})
			const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/save-notes`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify({ notes, date })
			})
			if (!response.ok) {
				throw new Error("Failed to save notes")
			}

			// Make the same API call with tomorrow's date
			const response_tomorrow = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/api/save-notes`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`
					},
					body: JSON.stringify({ notes, date: date_tomorrow })
				}
			)
			if (!response_tomorrow.ok) {
				throw new Error("Failed to save notes for tomorrow")
			}
			alert("Notes saved successfully!")
		} catch (error) {
			logging.error("Error saving notes:", error)
		}
		// `getAccessTokenSilently` is stable across renders per @auth0/auth0-react; `logging` and `date`/`date_tomorrow` are read at call time.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [notes])

	return (
		<div className="my-5 rounded-lg bg-white">
			<h2 className="font-bold text-2xl flex items-center mb-4 text-gray-800">
				<span className="mr-2 text-customBlue">
					<TfiWrite />
				</span>
				Daily Notes
			</h2>

			<textarea
				className="sensitive w-full min-h-[160px] rounded-lg p-4 bg-gray-50 focus:bg-white resize-y text-gray-700 focus:outline-none"
				value={notes}
				onChange={handleChange}
				placeholder="Type your notes here..."
				style={{ outline: "none" }}
			/>
			<div className="absolute bottom-2 right-2 text-xs text-gray-400 italic">
				{notes?.length > 0 ? `${notes.length} characters` : "Click to start typing"}
			</div>

			<button
				onClick={handleSaveNotes}
				className="mt-4 bg-customBlue text-white rounded-lg px-5 py-2.5 font-medium transition duration-300 ease-in-out hover:bg-blue-600 hover:shadow-md flex items-center justify-center"
			>
				<CiSaveDown2 className="mr-2 text-lg" />
				Save Notes
			</button>
		</div>
	)
}

// Memoize so it does not re-render if parent re-renders for other reasons
export default React.memo(NotesSection)
